/**
 * V44: 내 휴가 현황 테이블 컴포넌트
 *
 * HR 휴가현황 테이블과 동일한 컬럼/렌더링 방식 사용
 * - 사번, 이름, 조직, 발생, 사용, 선사용, 잔여, 적용시작, 적용종료, 1~12월, 퍼힐1Q~4Q
 * - 수정 버튼 컬럼 제외 (내 정보 수정 불가)
 * - 단일 행만 표시 (로그인 사용자)
 *
 * V41 규칙 적용:
 * - 적용시작: periodStart ?? validFrom
 * - 적용종료: periodEnd ?? validUntil
 *
 * V189: 월별 사용량을 leaveHistories SSoT 기반으로 조회
 * - leaveBalances.annual.monthlyUsage 캐시 대신 실시간 이력 집계
 * - source='import' 이력은 월별 분해 불가 → 총 사용에만 반영
 */

import React from 'react';
import { ExtendedLeaveBalance } from '../../../shared/hooks/useLeaveBalances';
import { useLeaveMonthlyUsage } from '../../../shared/hooks/useLeaveMonthlyUsage';
import { Users } from 'lucide-react';

interface MyLeaveStatusTableProps {
  balance: ExtendedLeaveBalance | null;
  isLoading: boolean;
  error: Error | null;
  /** V62: CN 인턴 여부 — true이면 적용기간을 '-'로 표시 (연차 없음) */
  isCnIntern?: boolean;
  /** V68: 퍼스널힐링데이 컬럼 미노출 (CN 비정규직 등 정책 미해당 대상) */
  hidePersonalHealing?: boolean;
}

/**
 * V44: 내 휴가 현황 테이블
 *
 * 가로 스크롤 가능한 단일 행 테이블
 * HR 휴가현황 페이지와 동일한 컬럼 구조
 */
export function MyLeaveStatusTable({ balance, isLoading, error, isCnIntern, hidePersonalHealing }: MyLeaveStatusTableProps) {
  // V189: 월별 사용량 조회 (leaveHistories SSoT)
  const monthlyUsageResult = useLeaveMonthlyUsage(
    balance?.employeeId,
    balance?.annual
      ? {
          validFrom: balance.annual.validFrom,
          validUntil: balance.annual.validUntil,
          periodStart: balance.annual.periodStart,
          periodEnd: balance.annual.periodEnd,
        }
      : undefined,
    'annual'
  );

  // V55.52.3: 로딩/에러/빈상태 스타일도 축소 적용
  // 로딩 상태
  if (isLoading) {
    return (
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Users size={18} />
          내 휴가 현황
        </h2>
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-gray-500)', fontSize: '13px' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Users size={18} />
          내 휴가 현황
        </h2>
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-red-500)', fontSize: '13px' }}>
          휴가 현황을 불러오는데 실패했습니다: {error.message}
        </div>
      </div>
    );
  }

  // 데이터 없음
  if (!balance) {
    return (
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Users size={18} />
          내 휴가 현황
        </h2>
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-gray-500)', fontSize: '13px' }}>
          휴가 현황 정보가 없습니다.
        </div>
      </div>
    );
  }

  // V189: 월별 사용량 (leaveHistories SSoT 기반)
  // V65-FIX(2026-04-22): 우선순위 반전 — balance(DB) 우선, histories fallback
  // 이유: useLeaveMonthlyUsage는 source='import' 레코드를 월별 분해 못함 →
  // import 포함 월이 누락됨. BE가 재계산한 DB(leaveBalances.annual.monthlyUsage)를 우선한다.
  const historyUsage = monthlyUsageResult.monthlyUsage;
  const historyIsLoading = monthlyUsageResult.isLoading;
  const balanceMonthlyUsage = balance.annual?.monthlyUsage || {};
  const monthlyUsage: Record<number | string, number> = {};
  if (historyIsLoading) {
    // histories 로딩 중 → balance 캐시만 표시 (깜빡임 방지)
    Object.assign(monthlyUsage, balanceMonthlyUsage);
  } else {
    // histories 로드 완료 → 월별 병합 (balance 우선, 빈 월은 histories fallback)
    for (let m = 1; m <= 12; m++) {
      if ((balanceMonthlyUsage as Record<number, number>)[m] !== undefined) {
        monthlyUsage[m] = (balanceMonthlyUsage as Record<number, number>)[m];
      } else if (historyUsage[m] !== undefined) {
        monthlyUsage[m] = historyUsage[m];
      }
    }
  }

  // 퍼스널힐링데이 분기별 사용 여부
  const q1 = balance.personalHealing?.q1Used;
  const q2 = balance.personalHealing?.q2Used;
  const q3 = balance.personalHealing?.q3Used;
  const q4 = balance.personalHealing?.q4Used;

  // V44.1: 적용기간 (validFrom/validUntil 우선, periodStart/periodEnd 폴백)
  // SSoT 규칙: validFrom/validUntil이 정식 필드, period*는 레거시 폴백용
  // V62: CN 인턴은 연차 없음 → 적용기간 '-' 표시
  const displayPeriodStart = isCnIntern ? '-' : (balance.annual?.validFrom ?? balance.annual?.periodStart ?? '-');
  const displayPeriodEnd = isCnIntern ? '-' : (balance.annual?.validUntil ?? balance.annual?.periodEnd ?? '-');

  // 헤더 스타일 (공통)
  const thStyle: React.CSSProperties = {
    padding: '6px 2px',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    borderRight: '1px solid var(--color-gray-100)',
    backgroundColor: 'var(--color-gray-50)',
  };

  // 셀 스타일 (공통)
  const tdStyle: React.CSSProperties = {
    padding: '6px 2px',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '12px',
    borderRight: '1px solid var(--color-gray-100)',
  };

  // V55.52.3: 패딩/마진 축소 - 작은 화면 최적화
  return (
    <div
      style={{
        background: 'white',
        padding: '16px', // V55.52.3: 20→16px
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '16px', // V55.52.3: 24→16px
      }}
    >
      <h2
        style={{
          fontSize: '16px', // V55.52.3: 18→16px
          fontWeight: 'bold',
          marginBottom: '12px', // V55.52.3: 16→12px
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <Users size={18} />
        내 휴가 현황
      </h2>

      {/* 가로 스크롤 컨테이너 */}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            border: '1px solid var(--color-gray-100)',
            width: '100%',
            minWidth: '900px',
          }}
        >
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
              {/* V55.52.4: 사번/조직 컬럼 제거 → 이름/발생/사용/선사용/잔여/적용시작/적용종료/1~12월/퍼힐1~4분기 */}
              <th style={thStyle}>이름</th>
              <th style={thStyle}>발생</th>
              <th style={thStyle}>사용</th>
              <th style={thStyle}>선사용</th>
              <th style={thStyle}>잔여</th>
              <th style={thStyle}>적용시작</th>
              <th style={thStyle}>적용종료</th>
              {/* 월별 사용량 1~12월 */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                <th key={`month-header-${m}`} style={thStyle}>
                  {m}월
                </th>
              ))}
              {/* 퍼힐 1~4분기 — V68: hidePersonalHealing=true 면 컬럼 자체 미노출 */}
              {!hidePersonalHealing && (
                <>
                  <th style={{ ...thStyle, fontSize: '11px' }}>퍼힐1Q</th>
                  <th style={{ ...thStyle, fontSize: '11px' }}>퍼힐2Q</th>
                  <th style={{ ...thStyle, fontSize: '11px' }}>퍼힐3Q</th>
                  <th style={{ ...thStyle, fontSize: '11px', borderRight: 'none' }}>퍼힐4Q</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
              {/* V55.52.4: 사번/조직 컬럼 제거 */}
              {/* 이름 */}
              <td style={tdStyle}>{balance.employeeName ?? '-'}</td>
              {/* 발생 */}
              <td style={tdStyle}>{balance.annual?.granted ?? 0}</td>
              {/* 사용 */}
              <td style={tdStyle}>{balance.annual?.used ?? 0}</td>
              {/* 선사용 */}
              <td
                style={{
                  ...tdStyle,
                  color: (balance.annual?.preUsed ?? 0) > 0 ? 'var(--color-orange-600)' : undefined,
                }}
              >
                {balance.annual?.preUsed ?? 0}
              </td>
              {/* 잔여 - V51: 마이너스 잔여일 때 빨간색 강조 */}
              <td style={{ ...tdStyle, color: ((balance.annual?.remaining ?? 0) < 0) ? 'var(--color-red-600)' : undefined, fontWeight: ((balance.annual?.remaining ?? 0) < 0) ? 600 : undefined }}>{balance.annual?.remaining ?? 0}</td>
              {/* 적용시작 */}
              <td style={tdStyle}>{displayPeriodStart}</td>
              {/* 적용종료 */}
              <td style={tdStyle}>{displayPeriodEnd}</td>
              {/* 1~12월 */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                const val = monthlyUsage[m];
                return (
                  <td key={`month-${m}`} style={tdStyle}>
                    {val !== undefined ? val : '-'}
                  </td>
                );
              })}
              {/* 퍼힐 1~4분기 (사용/미사용) - V45.2: 볼드 처리
                  V68: hidePersonalHealing=true 면 컬럼 자체 미노출 (thead 와 동일 조건) */}
              {!hidePersonalHealing && (
                <>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: '12px',
                      fontWeight: 600,
                      color: q1 ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                    }}
                  >
                    {q1 ? '사용' : '미사용'}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: '12px',
                      fontWeight: 600,
                      color: q2 ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                    }}
                  >
                    {q2 ? '사용' : '미사용'}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: '12px',
                      fontWeight: 600,
                      color: q3 ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                    }}
                  >
                    {q3 ? '사용' : '미사용'}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: '12px',
                      fontWeight: 600,
                      color: q4 ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                      borderRight: 'none',
                    }}
                  >
                    {q4 ? '사용' : '미사용'}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MyLeaveStatusTable;
